import { Component } from '@theme/component';

/**
 * Fit Finder — recommends a size from height / weight / build and drives the
 * real Horizon variant picker so cart, checkout and section re-rendering stay
 * completely native.
 *
 * The sizing logic lives in `recommendSize` and is fed by a merchant-editable
 * chart, so it can be refined later without touching the markup.
 */

const STORAGE_KEY = 'altior:fit-profile';

/* -------------------------------------------------------------------------- */
/* Stored profile                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Reads the visitor's saved measurements.
 * Storage can throw outright in private modes, so every access is guarded.
 *
 * @returns {{height?: number, weight?: number, build?: string} | null}
 */
function readProfile() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** @param {{height?: number, weight?: number, build?: string}} profile */
function writeProfile(profile) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch {
    /* Storage unavailable — the finder still works for this page view. */
  }
}

/* -------------------------------------------------------------------------- */
/* Sizing logic                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Parses the merchant-editable size chart.
 * One rule per line: `SIZE | minHeight-maxHeight | minWeight-maxWeight`
 *
 * @param {string} source
 * @returns {{size: string, hMin: number, hMax: number, wMin: number, wMax: number}[]}
 */
export function parseChart(source) {
  /** @type {{size: string, hMin: number, hMax: number, wMin: number, wMax: number}[]} */
  const rules = [];

  for (const line of String(source ?? '').split('\n')) {
    const parts = line.split('|').map((part) => part.trim());
    if (parts.length < 3) continue;

    const [size, heightRange, weightRange] = parts;
    const height = parseRange(heightRange);
    const weight = parseRange(weightRange);

    if (!size || !height || !weight) continue;

    rules.push({ size, hMin: height[0], hMax: height[1], wMin: weight[0], wMax: weight[1] });
  }

  return rules;
}

/**
 * @param {string} text
 * @returns {[number, number] | null}
 */
function parseRange(text) {
  const match = String(text).match(/(\d+(?:\.\d+)?)\s*(?:-|–|to)\s*(\d+(?:\.\d+)?)/i);
  if (!match) return null;

  const min = Number(match[1]);
  const max = Number(match[2]);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;

  return min <= max ? [min, max] : [max, min];
}

/**
 * Scores how well a measurement sits inside a range: 1 when inside, tapering
 * linearly to 0 once it is `tolerance` beyond either edge.
 *
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @param {number} tolerance
 */
function fitScore(value, min, max, tolerance) {
  if (value >= min && value <= max) return 1;
  const distance = value < min ? min - value : value - max;
  return Math.max(0, 1 - distance / tolerance);
}

// Weight separates sizes more sharply than height for upper-body garments.
const WEIGHT_INFLUENCE = 0.65;
const HEIGHT_INFLUENCE = 0.35;
const HEIGHT_TOLERANCE_CM = 12;
const WEIGHT_TOLERANCE_KG = 9;

/**
 * Recommends a size and reports how confident that recommendation is.
 *
 * @param {object} input
 * @param {{size: string, hMin: number, hMax: number, wMin: number, wMax: number}[]} input.chart
 * @param {number} input.height - Height in cm.
 * @param {number} input.weight - Weight in kg.
 * @param {number} [input.buildShift] - Steps to move along the chart for body build.
 * @returns {{size: string, confidence: number, shifted: boolean} | null}
 */
export function recommendSize({ chart, height, weight, buildShift = 0 }) {
  if (!chart.length || !Number.isFinite(height) || !Number.isFinite(weight)) return null;

  const scored = chart
    .map((rule) => ({
      rule,
      score:
        fitScore(height, rule.hMin, rule.hMax, HEIGHT_TOLERANCE_CM) * HEIGHT_INFLUENCE +
        fitScore(weight, rule.wMin, rule.wMax, WEIGHT_TOLERANCE_KG) * WEIGHT_INFLUENCE,
    }))
    .sort((a, b) => b.score - a.score);

  const best = scored[0];
  if (!best || best.score <= 0) return null;

  const runnerUp = scored[1];
  // A clear gap to the next-best size adds confidence, but neighbouring sizes
  // genuinely overlap, so how well the body fits the band carries most of it.
  const separation = runnerUp ? Math.min(1, (best.score - runnerUp.score) / 0.25) : 1;
  const raw = best.score * 0.85 + separation * 0.15;

  const order = chart.map((rule) => rule.size);
  const bestIndex = order.indexOf(best.rule.size);
  const shiftedIndex = Math.min(order.length - 1, Math.max(0, bestIndex + buildShift));
  const shifted = shiftedIndex !== bestIndex;

  // Never present the result as a certainty — it is an estimate from two numbers.
  let confidence = Math.round(Math.min(0.97, Math.max(0.55, raw)) * 100);
  if (shifted) confidence = Math.max(55, confidence - 6);

  return { size: order[shiftedIndex], confidence, shifted };
}

/* -------------------------------------------------------------------------- */
/* Variant picker bridge                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Finds the variant picker for the product this page is about, ignoring the
 * pickers inside recommendation and quick-add cards.
 *
 * The element is `<variant-picker>` — see the bottom of assets/variant-picker.js
 * — not `<variant-picker-component>` like most of Horizon's custom elements.
 *
 * @returns {Element | null}
 */
function mainVariantPicker() {
  return (
    document.querySelector('variant-picker[data-template-product-match="true"]') ??
    document.querySelector('.product-details variant-picker') ??
    document.querySelector('variant-picker')
  );
}

/**
 * Returns the radio inputs for one product option in the main picker.
 *
 * @param {string} optionName
 * @returns {HTMLInputElement[]}
 */
function optionInputs(optionName) {
  const picker = mainVariantPicker();
  if (!picker) return [];

  return /** @type {HTMLInputElement[]} */ (
    Array.from(picker.querySelectorAll('input[type="radio"]')).filter(
      (input) => input instanceof HTMLInputElement && input.dataset.optionName === optionName
    )
  );
}

/**
 * Selects a value on the real variant picker by clicking its radio, so Horizon
 * runs its own change handling (section re-render, price, media, cart state).
 *
 * @param {string} optionName
 * @param {string} value
 * @returns {boolean} Whether a matching, selectable input was found.
 */
export function selectVariantOption(optionName, value) {
  const input = optionInputs(optionName).find((candidate) => candidate.value === value);
  if (!input || input.disabled) return false;
  // Clicking an already-checked radio fires nothing, which is what we want.
  if (!input.checked) input.click();
  return true;
}

/* -------------------------------------------------------------------------- */
/* <fit-finder-component>                                                     */
/* -------------------------------------------------------------------------- */

/**
 * @typedef {object} FitFinderRefs
 * @property {HTMLInputElement} [height]
 * @property {HTMLInputElement} [weight]
 * @property {HTMLSelectElement} [build]
 * @property {HTMLElement} [result]
 * @property {HTMLElement} [resultSize]
 * @property {HTMLElement} [resultConfidence]
 * @property {HTMLElement} [resultNote]
 * @property {HTMLElement} [help]
 *
 * @extends Component<FitFinderRefs>
 */
export class FitFinder extends Component {
  /** @type {{size: string, confidence: number, shifted: boolean} | null} */
  #recommendation = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #debounce;

  #applying = false;

  /**
   * A variant change re-renders this section, and the server has no idea what
   * the shopper typed. `updatedCallback` cannot carry this: the replacement
   * tree comes from DOMParser, so its elements are never upgraded and morph's
   * `instanceof Component` check does not match them. Watching our own subtree
   * is independent of how the re-render is delivered.
   */
  #observer = new MutationObserver(() => this.#reapply());

  connectedCallback() {
    super.connectedCallback();
    this.#restore();
    // A shopper who already gave their measurements sees the answer straight away.
    const toggle = this.querySelector('details');
    if (toggle instanceof HTMLDetailsElement && this.refs.height instanceof HTMLInputElement && this.refs.height.value) {
      toggle.open = true;
    }
    // Show the recommendation for a restored profile, but do not change the
    // variant: the shopper may have arrived on a specific ?variant= URL, and
    // selecting for them would re-render the section on every page load.
    this.#evaluate({ apply: false });
    this.#syncModelNote();
    document.addEventListener('change', this.#handleColourChange);

    this.#observer.observe(this, { childList: true, subtree: true, characterData: true });
  }

  /**
   * The model note can differ per colour ("Colour | note" lines in the block
   * settings). All lines are rendered; this shows the one for the colour that
   * is checked in the variant picker, falling back to the general note.
   */
  #syncModelNote() {
    const note = this.querySelector('[data-model-note]');
    if (!note) return;
    const checked = document.querySelector('variant-picker input[name^="Color"]:checked, variant-picker input[name^="Colour"]:checked');
    const colour = checked instanceof HTMLInputElement ? checked.value.trim().toLowerCase() : '';
    const lines = /** @type {HTMLElement[]} */ ([...note.querySelectorAll('[data-model-colour]')]);
    const match = colour ? lines.find((line) => line.dataset.modelColour === colour) : undefined;
    const shown = match ?? lines.find((line) => line.dataset.modelColour === '');
    for (const line of lines) line.hidden = line !== shown;
  }

  #handleColourChange = (/** @type {Event} */ event) => {
    if (event.target instanceof HTMLInputElement && event.target.closest('variant-picker')) this.#syncModelNote();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
    document.removeEventListener('change', this.#handleColourChange);
    clearTimeout(this.#debounce);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#reapply();
  }

  /** Re-applies the state this component owns after something rewrote our DOM. */
  #reapply() {
    if (this.#applying) return;
    this.#applying = true;

    this.#restore();
    this.#evaluate({ apply: false });
    this.#syncModelNote();

    // Discard the records our own writes just queued, so this cannot re-enter.
    this.#observer.takeRecords();
    this.#applying = false;
  }

  get #optionName() {
    return this.dataset.optionName || 'Size';
  }

  get #chart() {
    return parseChart(this.dataset.chart ?? '');
  }

  /** Reads the measurements currently in the form. */
  get #measurements() {
    const height = Number(this.refs.height?.value);
    const weight = Number(this.refs.weight?.value);
    const build = this.refs.build?.value ?? 'regular';

    return {
      height: Number.isFinite(height) && height > 0 ? height : null,
      weight: Number.isFinite(weight) && weight > 0 ? weight : null,
      build,
    };
  }

  /** Restores saved measurements into the form, without clobbering typed input. */
  #restore() {
    const profile = readProfile();
    if (!profile) return;

    const { height, weight, build } = this.refs;

    if (height instanceof HTMLInputElement && !height.value && profile.height) {
      height.value = String(profile.height);
    }
    if (weight instanceof HTMLInputElement && !weight.value && profile.weight) {
      weight.value = String(profile.weight);
    }
    if (build instanceof HTMLSelectElement && profile.build) {
      const exists = Array.from(build.options).some((option) => option.value === profile.build);
      if (exists) build.value = profile.build;
    }
  }

  /**
   * Handles typing or changing any of the three inputs.
   *
   * Selecting a variant re-renders the whole section, so this waits for a pause
   * in typing rather than firing on every keystroke.
   */
  handleInput() {
    clearTimeout(this.#debounce);
    this.#debounce = setTimeout(() => {
      const { height, weight, build } = this.#measurements;
      if (height && weight) writeProfile({ height, weight, build });

      this.#evaluate({ apply: true });
    }, 350);
  }

  /**
   * Recomputes the recommendation, and optionally applies it to the picker.
   *
   * @param {{apply?: boolean}} [options]
   */
  #evaluate({ apply = false } = {}) {
    const { height, weight, build } = this.#measurements;

    // Plausibility guard — nonsense input should not produce a confident answer.
    const usable = height && weight && height >= 120 && height <= 220 && weight >= 30 && weight <= 200;

    this.#recommendation = usable
      ? recommendSize({
          chart: this.#chart,
          height,
          weight,
          buildShift: this.#buildShift(build),
        })
      : null;

    this.#render();

    const tick = this.refs.resultTick;
    if (tick instanceof HTMLElement) {
      const size = this.#recommendation?.size ?? '';
      if (!size) {
        tick.removeAttribute('data-added');
      } else if (apply && tick.dataset.size !== size && window.AltiorMotion) {
        window.AltiorMotion.playTick(tick);
      } else {
        tick.setAttribute('data-added', 'true');
      }
      tick.dataset.size = size;
    }

    if (apply && this.#recommendation && this.dataset.autoSelect !== 'false') {
      selectVariantOption(this.#optionName, this.#recommendation.size);
    }
  }

  /**
   * @param {string} build
   * @returns {number}
   */
  #buildShift(build) {
    const step = Number(this.dataset.buildStep ?? 1) || 1;
    if (build === 'slim') return -step;
    if (build === 'athletic' || build === 'broad') return step;
    return 0;
  }

  /** Paints the recommendation panel and the recommended-size marker. */
  #render() {
    const { result, resultSize, resultConfidence, resultNote } = this.refs;
    const recommendation = this.#recommendation;

    if (result instanceof HTMLElement) {
      result.classList.toggle('fit-finder__result--visible', Boolean(recommendation));
    }

    if (recommendation) {
      if (resultSize instanceof HTMLElement) resultSize.textContent = recommendation.size;
      if (resultConfidence instanceof HTMLElement) {
        resultConfidence.textContent = `${recommendation.confidence}% confidence`;
      }
    }

    // Say so plainly when the size we recommend is the one that's out of stock.
    const recommendedInput = recommendation
      ? optionInputs(this.#optionName).find((input) => input.value === recommendation.size)
      : null;
    const soldOut = Boolean(recommendedInput) && recommendedInput?.dataset.optionAvailable === 'false';

    if (resultNote instanceof HTMLElement) {
      resultNote.textContent = soldOut ? (this.dataset.soldOutNote ?? '') : '';
      resultNote.classList.toggle('fit-finder__note--visible', soldOut);
    }
  }
}

if (!customElements.get('fit-finder-component')) {
  customElements.define('fit-finder-component', FitFinder);
}
